"use client";

// Segment-level error boundary. Next.js wraps every route segment in this
// boundary automatically — anything thrown during rendering of a page in
// `src/app/**` that isn't caught by a closer error.tsx ends up here.
//
// We capture the error to PostHog (in addition to whatever onRequestError
// already did server-side, which is intentional — the server side hook may
// or may not have fired depending on whether the throw happened during SSR
// or client-side hydration / interaction).

import { useEffect } from "react";
import posthog from "posthog-js";
import { Button } from "@/components/ui/button";
import { ButtonLink } from "@/components/button-link";

export default function SegmentError({
  error,
  reset,
}: {
  // Next.js gives the original Error plus an optional `digest` it uses to
  // correlate server-side and client-side instances of the same error.
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Skip Next.js's synthetic control-flow throws — they reach this
    // boundary in some hydration paths but are not real errors. The
    // posthog-js init also drops them via before_send; this is belt-and-
    // braces in case the message ever changes shape.
    if (error.message === "NEXT_REDIRECT" || error.message === "NEXT_NOT_FOUND") {
      return;
    }
    posthog.captureException(error, {
      boundary: "app/error.tsx",
      digest: error.digest,
      // `error_name` + `online` make opaque network failures (Safari's
      // `TypeError: Load failed`) diagnosable: a connected device that still
      // failed points at the server or service worker, not the user's link.
      error_name: error.name,
      online: typeof navigator !== "undefined" ? navigator.onLine : null,
    });
  }, [error]);

  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-6 py-16 text-center">
      <h2 className="font-display text-xl">Something went wrong</h2>
      <p className="mt-3 text-sm text-muted-foreground">
        We&apos;ve logged the error so it can be looked into. Try again, or
        head back to the home screen.
      </p>
      {error.digest && (
        <p className="mt-2 font-mono text-[11px] text-muted-foreground/70">
          ref: {error.digest}
        </p>
      )}
      <div className="mt-6 flex gap-2">
        <Button variant="outline" onClick={reset}>
          Try again
        </Button>
        <ButtonLink href="/">Home</ButtonLink>
      </div>
    </div>
  );
}
